import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { financeHotspots, type FinanceHotspot } from '@/mocks/finance';
import { loadLiveFinance, requestFinanceRefresh } from '@/lib/finance';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';

export type FinanceHotspotWithMeta = FinanceHotspot & { isNew?: boolean };

interface PersistedState {
  lastUpdatedAt: string | null;
  autoRefreshEnabled: boolean;
  refreshInterval: number;
}

const STORAGE_KEY = 'personal-desk-finance-refresh';

function loadPersisted(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { lastUpdatedAt: null, autoRefreshEnabled: true, refreshInterval: 30 };
    const parsed = JSON.parse(raw);
    return {
      lastUpdatedAt: parsed.lastUpdatedAt || null,
      autoRefreshEnabled: parsed.autoRefreshEnabled ?? true,
      refreshInterval: parsed.refreshInterval ?? 30,
    };
  } catch {
    return { lastUpdatedAt: null, autoRefreshEnabled: true, refreshInterval: 30 };
  }
}

function savePersisted(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '刚刚更新';
  if (diffMin < 60) return `${diffMin} 分钟前`;
  const h = date.getHours().toString().padStart(2, '0');
  const m = date.getMinutes().toString().padStart(2, '0');
  return `今天 ${h}:${m}`;
}

export interface UseFinanceRefreshReturn {
  lastUpdatedAt: Date | null;
  isRefreshing: boolean;
  autoRefreshEnabled: boolean;
  refreshInterval: number;
  currentItems: FinanceHotspotWithMeta[];
  isStale: boolean;
  nextUpdateTime: string | null;
  formattedLastUpdated: string;
  dataMode: 'live' | 'demo';
  refreshError: string | null;
  triggerRefresh: () => Promise<void>;
  setAutoRefreshEnabled: (v: boolean) => void;
  setRefreshInterval: (v: number) => void;
}

export function useFinanceRefresh(onComplete?: (mode: 'live' | 'demo', message?: string) => void): UseFinanceRefreshReturn {
  const [saved] = useState(loadPersisted);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(saved.lastUpdatedAt ? new Date(saved.lastUpdatedAt) : null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(saved.autoRefreshEnabled);
  const [refreshInterval, setRefreshInterval] = useState(saved.refreshInterval);
  const [currentItems, setCurrentItems] = useState<FinanceHotspotWithMeta[]>(financeHotspots);
  const [dataMode, setDataMode] = useState<'live' | 'demo'>('demo');
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const isStale = useMemo(() => {
    if (!lastUpdatedAt) return false;
    return Date.now() - lastUpdatedAt.getTime() > Math.max(refreshInterval * 2, 30) * 60 * 1000;
  }, [lastUpdatedAt, refreshInterval]);

  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdatedAt) return '尚未更新';
    return formatRelativeTime(lastUpdatedAt);
  }, [lastUpdatedAt]);

  const nextUpdateTime = useMemo(() => {
    if (!autoRefreshEnabled || !lastUpdatedAt) return null;
    const next = new Date(lastUpdatedAt.getTime() + refreshInterval * 60 * 1000);
    const h = next.getHours().toString().padStart(2, '0');
    const m = next.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }, [lastUpdatedAt, autoRefreshEnabled, refreshInterval]);

  // Persist whenever key state changes
  const persist = useCallback(
    (updatedAt: Date | null = lastUpdatedAt) => {
      savePersisted({
        lastUpdatedAt: updatedAt?.toISOString() || null,
        autoRefreshEnabled,
        refreshInterval,
      });
    },
    [lastUpdatedAt, autoRefreshEnabled, refreshInterval]
  );

  // Watch for changes from other tabs/pages via storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        const parsed = JSON.parse(e.newValue || '{}');
        if (parsed.lastUpdatedAt) {
          const newDate = new Date(parsed.lastUpdatedAt);
          if (newDate.getTime() !== lastUpdatedAt?.getTime()) {
            setLastUpdatedAt(newDate);
          }
        }
        if (typeof parsed.autoRefreshEnabled === 'boolean') {
          setAutoRefreshEnabled(parsed.autoRefreshEnabled);
        }
        if (typeof parsed.refreshInterval === 'number') {
          setRefreshInterval(parsed.refreshInterval);
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [lastUpdatedAt]);

  const loadLatest = useCallback(async () => {
    try {
      const live = await loadLiveFinance();
      if (!live?.items.length) return false;
      setCurrentItems(live.items.map((item, index) => ({ ...item, isNew: index < 2 })));
      setLastUpdatedAt(live.fetchedAt);
      setDataMode('live');
      setRefreshError(null);
      persist(live.fetchedAt);
      return true;
    } catch {
      setRefreshError('实时财经暂时无法更新，请稍后重试');
      return false;
    }
  }, [persist]);

  const triggerRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (!isSupabaseConfigured) {
        onComplete?.('demo', 'Supabase 尚未配置，当前为财经演示数据');
        return;
      }
      await requestFinanceRefresh();
      const loaded = await loadLatest();
      onComplete?.(loaded ? 'live' : 'demo', loaded ? undefined : '暂无真实财经数据，当前为演示数据');
    } catch {
      const message = '实时财经更新服务尚未部署，请先配置数据源';
      setRefreshError(message);
      onComplete?.('demo', message);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, loadLatest, onComplete]);

  // Persist on interval/enable changes
  useEffect(() => {
    persist();
  }, [autoRefreshEnabled, refreshInterval, persist]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    void loadLatest();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void loadLatest();
    };
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const channel = supabase
      ?.channel('finance-hotspots')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'finance_hotspots' }, () => void loadLatest())
      .subscribe();
    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      if (channel) void supabase?.removeChannel(channel);
    };
  }, [loadLatest]);

  useEffect(() => {
    if (!autoRefreshEnabled || !isSupabaseConfigured) return;
    const interval = window.setInterval(() => void loadLatest(), refreshInterval * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [autoRefreshEnabled, loadLatest, refreshInterval]);

  return {
    lastUpdatedAt,
    isRefreshing,
    autoRefreshEnabled,
    refreshInterval,
    currentItems,
    isStale,
    nextUpdateTime,
    formattedLastUpdated,
    dataMode,
    refreshError,
    triggerRefresh,
    setAutoRefreshEnabled,
    setRefreshInterval,
  };
}
